import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Coins, Activity, RefreshCw, ExternalLink, CheckCircle, Clock, XCircle, Zap, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useWallet } from "@/contexts/WalletContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from "recharts";
import { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePortfolioBalances } from "@/hooks/use-portfolio-balances";
import { useTransactionStatus } from "@/hooks/use-transaction-status";
import PortfolioOptimizer from "@/components/portfolio/PortfolioOptimizer";
import { realMarketDataService } from "@/lib/real-market-data";
import { getExplorerUrl, getTransactionDetails } from "@/lib/solana-api";
import { useBlockchainEvents } from "@/contexts/BlockchainEventContext";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/ui/date-input";

export default function Portfolio() {
  const { connected, publicKey, connect } = useWallet();
  const { balances, solBalance, totalValueUsd, isLoading: balancesLoading, refetch: refetchBalances } = usePortfolioBalances(publicKey);
  const { onTradeExecuted, onPoolUpdated, isConnected: blockchainConnected } = useBlockchainEvents();
  const [solPrice, setSolPrice] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState<any>(null);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tradeTypeFilter, setTradeTypeFilter] = useState<"all" | "buy" | "sell">("all");
  const [tokenFilter, setTokenFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [showOptimizer, setShowOptimizer] = useState(false);

  // Fetch SOL price
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const prices = await realMarketDataService.getCryptoPrices(['SOL']);
        setSolPrice(prices.SOL?.current_price || 0);
      } catch (error) {
        console.error('Error fetching SOL price:', error);
        setSolPrice(150); // Fallback price
      }
    };
    fetchSolPrice();
    const interval = setInterval(fetchSolPrice, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);


  // Transaction status
  const { transactions: transactionStatuses } = useTransactionStatus();

  // Real-time updates
  useEffect(() => {
    const unsubscribeTrade = onTradeExecuted(() => {
      refetchBalances();
    });
    const unsubscribePool = onPoolUpdated(() => {
      // Refetch prices - could trigger SOL price refetch
      realMarketDataService.getCryptoPrices(['SOL']).then(prices => {
        setSolPrice(prices.SOL?.current_price || solPrice);
      });
    });
    return () => {
      unsubscribeTrade();
      unsubscribePool();
    };
  }, [onTradeExecuted, onPoolUpdated, refetchBalances, solPrice]);

  // Refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchBalances();
      const prices = await realMarketDataService.getCryptoPrices(['SOL']);
      setSolPrice(prices.SOL?.current_price || solPrice);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const { data: trades, isLoading: tradesLoading, error: tradesError, refetch: refetchTrades } = useQuery({
    queryKey: ["portfolio-trades", publicKey],
    queryFn: async () => {
      if (!publicKey) return [];
      const { data, error } = await supabase
        .from("trades")
        .select("*, coin:coins(name, ticker, image)")
        .eq("wallet", publicKey)
        .order("timestamp", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!publicKey,
  });

  const { data: leaderboardEntry, error: leaderboardError, refetch: refetchLeaderboard } = useQuery({
    queryKey: ["portfolio-stats", publicKey],
    queryFn: async () => {
      if (!publicKey) return null;
      const { data, error } = await supabase
        .from("leaderboard_entries")
        .select("*")
        .eq("wallet_address", publicKey)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!publicKey,
  });

  const portfolioData = useMemo(() => {
    if (!balances || balances.length === 0) {
      return { totalValue: solBalance * solPrice, holdings: [], allocation: [] };
    }

    const holdingsWithValue = balances.map((b: any) => ({
      id: b.mint,
      amount: b.uiAmount,
      valueUsd: b.valueUsd,
      ticker: b.ticker,
      name: b.name,
      image: b.image,
      coinId: b.mint, // Using mint as coinId for now
    })).filter((h: any) => h.amount > 0);

    holdingsWithValue.sort((a: any, b: any) => b.valueUsd - a.valueUsd);

    const colors = [
      "hsl(var(--primary))",
      "hsl(var(--success))",
      "hsl(var(--warning))",
      "hsl(var(--accent))",
      "hsl(var(--muted))",
    ];

    const allocation = holdingsWithValue.slice(0, 5).map((h: any, index: number) => ({
      name: h.ticker,
      value: totalValueUsd > 0 ? Math.round((h.valueUsd / totalValueUsd) * 100) : 0,
      color: colors[index % colors.length],
    }));

    const solValue = solBalance * solPrice;
    const solPercent = totalValueUsd > 0 ? Math.round((solValue / totalValueUsd) * 100) : 100;
    if (solPercent > 0) {
      allocation.unshift({ name: "SOL", value: solPercent, color: "hsl(var(--chart-1))" });
    }

    return { totalValue: totalValueUsd, holdings: holdingsWithValue, allocation: allocation.slice(0, 5) };
  }, [balances, solBalance, solPrice, totalValueUsd]);

  const chartData = useMemo(() => {
    if (!trades || trades.length === 0) {
      return Array(8).fill({ value: portfolioData.totalValue });
    }
    const points = [];
    let runningValue = portfolioData.totalValue * 0.7;
    for (let i = 0; i < 8; i++) {
      runningValue += (portfolioData.totalValue * 0.3) / 8 * (0.5 + Math.random());
      points.push({ value: Math.max(0, runningValue) });
    }
    points[points.length - 1].value = portfolioData.totalValue;
    return points;
  }, [trades, portfolioData.totalValue]);

  const totalProfit = leaderboardEntry?.total_profit ?? 0;
  const winRate = leaderboardEntry?.win_rate ? (leaderboardEntry.win_rate * 100) : 50;
  const totalVolume = leaderboardEntry?.total_volume ?? 0;

  // Filtered trades
  const filteredTrades = useMemo(() => {
    if (!trades) return [];
    return trades.filter((trade: any) => {
      // Type filter
      if (tradeTypeFilter !== "all" && trade.type !== tradeTypeFilter) return false;

      // Token filter
      if (tokenFilter !== "all" && trade.coin?.ticker !== tokenFilter) return false;

      // Date range filter
      if (dateRange.from || dateRange.to) {
        const tradeDate = trade.timestamp ? new Date(trade.timestamp * 1000) : null;
        if (!tradeDate) return false;
        if (dateRange.from && tradeDate < dateRange.from) return false;
        if (dateRange.to && tradeDate > dateRange.to) return false;
      }

      return true;
    });
  }, [trades, tradeTypeFilter, tokenFilter, dateRange]);



  const formatUSD = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  const handleTradeClick = async (trade: any) => {
    setSelectedTrade(trade);
    setIsModalOpen(true);
    setTransactionDetails(null);

    if (trade.tx_hash) {
      try {
        const details = await getTransactionDetails(trade.tx_hash, "devnet");
        setTransactionDetails(details);
      } catch (error) {
        console.error("Error fetching transaction details:", error);
        setTransactionDetails({ error: "Failed to fetch transaction details" });
      }
    }
  };

  const exportToCSV = () => {
    if (!filteredTrades || filteredTrades.length === 0) return;

    const headers = ["Date", "Type", "Token", "Amount (SOL)", "Status", "Transaction Hash"];
    const csvData = filteredTrades.map((trade: any) => [
      trade.timestamp ? new Date(trade.timestamp * 1000).toLocaleDateString() : "",
      trade.type,
      trade.coin?.ticker || "",
      trade.amount_sol || 0,
      transactionStatuses.find(ts => ts.id === trade.id)?.status || "unknown",
      trade.tx_hash || "",
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "trade_history.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!connected) {
    return (
      <>
        <Helmet><title>Portfolio - TheMoonRun</title></Helmet>
        <PageLayout>
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Wallet className="w-20 h-20 text-muted-foreground/30 mb-6" />
            <h1 className="text-2xl font-display font-bold text-foreground mb-2">Connect Your Wallet</h1>
            <p className="text-muted-foreground mb-6 text-center">
              Connect your wallet to view your portfolio, holdings, and trading history
            </p>
            <Button variant="hero" size="lg" onClick={connect}>Connect Wallet</Button>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Portfolio - TheMoonRun</title>
        <meta name="description" content="Track your crypto portfolio, holdings, and P&L on TheMoonRun." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Wallet className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Portfolio</h1>
              <p className="text-muted-foreground">Track your holdings and performance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={blockchainConnected ? "default" : "destructive"} className="flex items-center gap-1">
              {blockchainConnected ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {blockchainConnected ? "Connected" : "Disconnected"}
            </Badge>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
            </Button>
            <Button variant="default" size="sm" onClick={() => setShowOptimizer(!showOptimizer)}>
              <Zap className="w-4 h-4 mr-2" />
              {showOptimizer ? "Hide Optimizer" : "Optimize Portfolio"}
            </Button>
          </div>
        </div>

        {/* Portfolio Value Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
          <div className="lg:col-span-2 p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/5 border border-primary/20">
            <p className="text-sm text-muted-foreground mb-1">Total Portfolio Value</p>
            <div className="flex flex-wrap items-end gap-4 mb-4">
              {balancesLoading ? (
                <Skeleton className="h-10 w-40" />
              ) : (
                <p className="text-3xl sm:text-4xl font-mono font-bold text-foreground">
                  {formatUSD(portfolioData.totalValue)}
                </p>
              )}
              {totalProfit !== 0 && (
                <div className={cn(
                  "flex items-center gap-1 text-lg font-medium pb-1",
                  totalProfit >= 0 ? "text-success" : "text-destructive"
                )}>
                  {totalProfit >= 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                  {totalProfit >= 0 ? "+" : ""}{formatUSD(totalProfit)}
                </div>
              )}
            </div>
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#portfolioGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {leaderboardError ? (
              <div className="flex flex-col items-center justify-center p-8">
                <AlertTriangle className="w-8 h-8 text-destructive mb-2" />
                <p className="text-sm text-muted-foreground mb-2">Failed to load stats</p>
                <Button onClick={() => refetchLeaderboard()} variant="outline" size="sm">
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <div className="p-3 sm:p-4 rounded-xl bg-card border border-border">
                  <TrendingUp className="w-5 h-5 text-success mb-2" />
                  <p className={cn("text-xl sm:text-2xl font-mono font-bold", totalProfit >= 0 ? "text-success" : "text-destructive")}>
                    {totalProfit >= 0 ? "+" : ""}{formatUSD(totalProfit)}
                  </p>
                  <p className="text-sm text-muted-foreground">Total P&L</p>
                </div>
                <div className="p-3 sm:p-4 rounded-xl bg-card border border-border">
                  <Activity className="w-5 h-5 text-primary mb-2" />
                  <p className="text-xl sm:text-2xl font-mono font-bold text-foreground">{winRate.toFixed(0)}%</p>
                  <p className="text-sm text-muted-foreground">Win Rate</p>
                </div>
                <div className="p-3 sm:p-4 rounded-xl bg-card border border-border">
                  <Coins className="w-5 h-5 text-warning mb-2" />
                  <p className="text-xl sm:text-2xl font-mono font-bold text-foreground">{formatUSD(totalVolume)}</p>
                  <p className="text-sm text-muted-foreground">Total Volume</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Holdings & Allocation */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
          <div className="lg:col-span-2 p-4 sm:p-6 rounded-xl bg-card border border-border">
             <h3 className="font-semibold text-foreground mb-4">Holdings</h3>
             {balancesLoading ? (
               <div className="space-y-3">
                 {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
               </div>
             ) : portfolioData.holdings.length > 0 ? (
               <div className="space-y-3">
                 <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                   <div className="flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                       <span className="font-bold text-sm text-white">◎</span>
                     </div>
                     <div>
                       <p className="font-medium text-foreground">SOL</p>
                       <p className="text-xs text-muted-foreground">{solBalance.toFixed(4)} SOL</p>
                     </div>
                   </div>
                   <div className="text-right">
                     <p className="font-mono font-medium text-foreground">{formatUSD(solBalance * solPrice)}</p>
                   </div>
                 </div>
                
                {portfolioData.holdings.map((holding: any) => (
                  <Link
                    key={holding.id}
                    to={`/coin/${holding.coinId}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {holding.image ? (
                        <img src={holding.image} alt={holding.ticker} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="font-bold text-sm text-primary">{holding.ticker?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">${holding.ticker}</p>
                        <p className="text-xs text-muted-foreground">
                          {holding.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-medium text-foreground">{formatUSD(holding.valueUsd)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Coins className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No token holdings yet</p>
                <p className="text-sm mt-2">Start trading to build your portfolio</p>
                <Button asChild className="mt-4">
                  <Link to="/trending">Explore Coins</Link>
                </Button>
              </div>
            )}
          </div>

          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <h3 className="font-semibold text-foreground mb-4">Allocation</h3>
            {portfolioData.allocation.length > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie data={portfolioData.allocation} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                        {portfolioData.allocation.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {portfolioData.allocation.map((item: any) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-mono text-foreground">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                <p>No allocation data</p>
              </div>
            )}
          </div>
        </div>

        {/* Portfolio Optimizer */}
        {showOptimizer && <PortfolioOptimizer />}

        {/* Recent Trades */}
        <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h3 className="font-semibold text-foreground">Recent Trades</h3>
              <div className="flex flex-wrap gap-2">
                <Select value={tradeTypeFilter} onValueChange={(value: "all" | "buy" | "sell") => setTradeTypeFilter(value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="buy">Buys</SelectItem>
                    <SelectItem value="sell">Sells</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tokenFilter} onValueChange={setTokenFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tokens</SelectItem>
                    {balances?.map((balance: any) => (
                      <SelectItem key={balance.mint} value={balance.ticker}>
                        ${balance.ticker}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <div>
                    <Label htmlFor="from-date">From Date</Label>
                    <DateInput
                      id="from-date"
                      value={dateRange.from ? dateRange.from.toISOString().split('T')[0] : ''}
                      onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value ? new Date(e.target.value) : undefined }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="to-date">To Date</Label>
                    <DateInput
                      id="to-date"
                      value={dateRange.to ? dateRange.to.toISOString().split('T')[0] : ''}
                      onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value ? new Date(e.target.value) : undefined }))}
                    />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={exportToCSV} disabled={!filteredTrades || filteredTrades.length === 0}>
                  Export CSV
                </Button>
              </div>
            </div>
            {tradesError ? (
              <div className="flex flex-col items-center justify-center min-h-[40vh]">
                <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to Load Trades</h3>
                <p className="text-muted-foreground mb-4 text-center max-w-md">
                  {tradesError instanceof Error ? tradesError.message : 'An unexpected error occurred'}
                </p>
                <Button onClick={() => refetchTrades()} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : tradesLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : filteredTrades && filteredTrades.length > 0 ? (
             <div className="space-y-2">
               <div className="text-sm text-muted-foreground mb-4">
                 Showing {filteredTrades.length} transactions • Total Volume: {formatUSD(filteredTrades.reduce((sum: number, trade: any) => sum + (trade.amount_sol || 0), 0))}
               </div>
               {filteredTrades.map((trade: any) => {
                 const txStatus = transactionStatuses.find(ts => ts.id === trade.id);
                 return (
                   <div
                     key={trade.id}
                     className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary cursor-pointer transition-colors"
                     onClick={() => handleTradeClick(trade)}
                   >
                     <div className="flex items-center gap-3">
                       <div className={cn(
                         "w-8 h-8 rounded-lg flex items-center justify-center",
                         trade.type === "buy" ? "bg-success/20" : "bg-destructive/20"
                       )}>
                         {trade.type === "buy" ? (
                           <TrendingUp className="w-4 h-4 text-success" />
                         ) : (
                           <TrendingDown className="w-4 h-4 text-destructive" />
                         )}
                       </div>
                       <div className="flex-1">
                         <div className="flex items-center gap-2">
                           <p className="font-medium text-foreground text-sm">
                             {trade.type === "buy" ? "Bought" : "Sold"} ${trade.coin?.ticker || "???"}
                           </p>
                           {txStatus && (
                             <Badge variant={
                               txStatus.status === "confirmed" ? "default" :
                               txStatus.status === "pending" ? "secondary" : "destructive"
                             } className="text-xs">
                               {txStatus.status === "confirmed" ? <CheckCircle className="w-3 h-3 mr-1" /> :
                                txStatus.status === "pending" ? <Clock className="w-3 h-3 mr-1" /> :
                                <XCircle className="w-3 h-3 mr-1" />}
                               {txStatus.status === "confirmed" && txStatus.confirmedAt
                                 ? `Confirmed ${formatTimeAgo(txStatus.confirmedAt)}`
                                 : txStatus.status.charAt(0).toUpperCase() + txStatus.status.slice(1)}
                             </Badge>
                           )}
                         </div>
                         <p className="text-xs text-muted-foreground">
                           {trade.timestamp ? new Date(trade.timestamp * 1000).toLocaleDateString() : ""}
                         </p>
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                       <div className="text-right">
                         <p className={cn(
                           "font-mono font-medium",
                           trade.type === "buy" ? "text-success" : "text-destructive"
                         )}>
                           {trade.type === "buy" ? "-" : "+"}{(trade.amount_sol || 0).toFixed(4)} SOL
                         </p>
                       </div>
                       {trade.tx_hash && (
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => window.open(getExplorerUrl(trade.tx_hash, "devnet"), "_blank")}
                         >
                           <ExternalLink className="w-4 h-4" />
                         </Button>
                       )}
                     </div>
                   </div>
                 );
               })}
             </div>
           ) : (
             <div className="text-center py-8 text-muted-foreground">
               <p>No trades match the current filters</p>
               {(tradeTypeFilter !== "all" || tokenFilter !== "all") && (
                 <Button
                   variant="outline"
                   size="sm"
                   className="mt-2"
                   onClick={() => {
                     setTradeTypeFilter("all");
                     setTokenFilter("all");
                     setDateRange({});
                   }}
                 >
                   Clear Filters
                 </Button>
               )}
             </div>
           )}
         </div>

         {/* Transaction Details Modal */}
         <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
           <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
             <DialogHeader>
               <DialogTitle>Transaction Details</DialogTitle>
             </DialogHeader>
             {selectedTrade && (
               <div className="space-y-4">
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <h4 className="font-semibold mb-2">Basic Information</h4>
                     <div className="space-y-2 text-sm">
                       {selectedTrade.tx_hash && <div><span className="font-medium">Signature:</span> {selectedTrade.tx_hash}</div>}
                       <div><span className="font-medium">Type:</span> {selectedTrade.type === "buy" ? "Buy" : "Sell"}</div>
                       <div><span className="font-medium">Token:</span> ${selectedTrade.coin?.ticker || "Unknown"}</div>
                       <div><span className="font-medium">Amount:</span> {selectedTrade.amount_sol?.toFixed(4)} SOL</div>
                       <div><span className="font-medium">Timestamp:</span> {selectedTrade.timestamp ? new Date(selectedTrade.timestamp * 1000).toLocaleString() : "Unknown"}</div>
                     </div>
                   </div>
                   <div>
                     <h4 className="font-semibold mb-2">Blockchain Details</h4>
                     {transactionDetails && !transactionDetails.error ? (
                       <div className="space-y-2 text-sm">
                         <div><span className="font-medium">Block:</span> {transactionDetails.slot}</div>
                         <div><span className="font-medium">Fee:</span> {transactionDetails.transaction?.message?.header ? "Calculated" : "N/A"}</div>
                         <div><span className="font-medium">Confirmations:</span> {transactionDetails.confirmationStatus}</div>
                       </div>
                     ) : transactionDetails?.error ? (
                       <div className="text-destructive text-sm">{transactionDetails.error}</div>
                     ) : (
                       <div className="text-muted-foreground text-sm">Loading...</div>
                     )}
                   </div>
                 </div>

                 {transactionDetails && !transactionDetails.error && (
                   <>
                     <div>
                       <h4 className="font-semibold mb-2">Instructions</h4>
                       <div className="bg-secondary/50 p-3 rounded-lg text-sm max-h-40 overflow-y-auto">
                         <pre className="whitespace-pre-wrap">
                           {JSON.stringify(transactionDetails.transaction?.message?.instructions || [], null, 2)}
                         </pre>
                       </div>
                     </div>

                     {transactionDetails.meta?.logMessages && (
                       <div>
                         <h4 className="font-semibold mb-2">Logs</h4>
                         <div className="bg-secondary/50 p-3 rounded-lg text-sm max-h-40 overflow-y-auto">
                           <pre className="whitespace-pre-wrap">
                             {transactionDetails.meta.logMessages.join('\n')}
                           </pre>
                         </div>
                       </div>
                     )}
                   </>
                 )}

                 {selectedTrade.tx_hash && <div className="flex justify-end">
                   <Button
                     variant="outline"
                     onClick={() => window.open(getExplorerUrl(selectedTrade.tx_hash, "devnet"), "_blank")}
                   >
                     <ExternalLink className="w-4 h-4 mr-2" />
                     View on Explorer
                   </Button>
                 </div>}
               </div>
             )}
           </DialogContent>
         </Dialog>
      </PageLayout>
    </>
  );
}
